"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import Image from "next/image";
import {
  motion,
  useMotionValue,
  animate,
  AnimatePresence,
} from "framer-motion";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import useWindowSize from "@/hooks/useWindowSize";
import { useAutoSlider } from "@/hooks/useAutoSlider";
import ActionButton from "@/components/Common/ActionButton";
import { Title, Description } from "@/components/Common/Typography";
import styles from "./partnerSection.module.css";

interface PartnerHeaderInfo {
  title?: string;
  desc?: string;
}

interface PartnerSectionProps {
  isActive?: boolean;
  partnerLogos?: string[];
  certificationImages?: string[];
  partnerHeader?: PartnerHeaderInfo;
  certHeader?: PartnerHeaderInfo;
  data?: any;
  locale?: string;
}

/* ------------------- MAIN SECTION ------------------- */
export default function PartnerSection({
  isActive,
  partnerLogos = [],
  certificationImages = [],
  partnerHeader,
  certHeader,
  locale = "vi",
}: PartnerSectionProps) {
  const { isMobile, isDesktop } = useWindowSize();

  const partnerTitle =
    partnerHeader?.title ||
    (locale === "vi" ? "ĐỐI TÁC CỦA CHÚNG TÔI" : "OUR PARTNERS");
  const partnerDesc =
    partnerHeader?.desc ||
    (locale === "vi"
      ? "Chúng tôi tự hào hợp tác cùng các thương hiệu uy tín hàng đầu Việt Nam và quốc tế."
      : "We are proud to partner with leading reputable brands in Vietnam and internationally.");

  const certTitle =
    certHeader?.title ||
    (locale === "vi" ? "CHỨNG NHẬN CHẤT LƯỢNG" : "QUALITY CERTIFICATION");
  const certDesc =
    certHeader?.desc ||
    (locale === "vi"
      ? "Các chứng nhận chất lượng sản phẩm, đảm bảo uy tín và niềm tin cho đối tác."
      : "Product quality certifications, ensuring prestige and trust for partners.");

  return (
    <section
      className={styles.section}
      style={{ marginTop: isMobile ? "0px" : !isDesktop ? "20px" : "10vh" }}
    >
      <div className={styles.container}>
        <div className={styles.grid}>
          {/* Vertical Divider Line */}
          {!isMobile && <div className={styles.dividerLine} />}

          {/* LEFT: PARTNERS */}
          <div
            className={styles.col}
            style={{ paddingRight: isMobile ? 0 : "2vw" }}
          >
            <SectionHeader
              title={partnerTitle}
              desc={partnerDesc}
              isMobile={isMobile}
            />
            <LeftPartnerSection logos={partnerLogos} />
          </div>

          {/* RIGHT: CERTIFICATIONS */}
          <div
            className={styles.col}
            style={{ paddingLeft: isMobile ? 0 : "2vw" }}
          >
            <SectionHeader
              title={certTitle}
              desc={certDesc}
              isMobile={isMobile}
            />
            <RightPartnerSection papers={certificationImages} />
          </div>
        </div>

        {/* BUTTON */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "32px",
          }}
        >
          <ActionButton
            label={locale === "vi" ? "Hợp tác kinh doanh" : "Business cooperation"}
            link={`/${locale}/lien-he`}
          />
        </div>
      </div>

      {/* DECORATIVE IMAGES */}
      {isDesktop && <DecoImages isActive={isActive} />}
    </section>
  );
}

/* ---------------- HEADER ---------------- */
function SectionHeader({
  title,
  desc,
}: {
  title: string;
  desc: string;
  isMobile: boolean;
}) {
  return (
    <div className={styles.header} style={{ textAlign: "center" }}>
      <Title
        size="small"
        align="center"
        // className={styles.headerTitle}
        style={{ marginBottom: "6px" }}
      >
        {title}
      </Title>
      <Description
        size="small"
        align="center"
        className={styles.headerDesc}
        style={{ margin: "0 auto" }}
      >
        {desc}
      </Description>
    </div>
  );
}

/* ------------------- LEFT PARTNER LOGOS ------------------- */
function LeftPartnerSection({ logos }: { logos: string[] }) {
  const { isMobile, isTablet } = useWindowSize();
  const containerRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [currentCol, setCurrentCol] = useState(0);

  const itemsPerCol = 2;
  const gap = 32;
  const itemSize = isMobile ? 60 : isTablet ? 100 : 130;
  const visibleCols = 3;

  const gridData: string[][] = [];
  for (let i = 0; i < logos.length; i += itemsPerCol) {
    gridData.push([logos[i], logos[i + 1]]);
  }

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, [isMobile]);

  const totalCols = gridData.length;
  const colWidth = itemSize + gap;
  const totalScrollWidth = totalCols * colWidth - gap;
  const maxScrollX = Math.max(totalScrollWidth - containerWidth, 0);

  const handleDragEnd = (_: any, info: any) => {
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    const direction = offset < 0 ? 1 : -1;
    let col = -x.get() / colWidth;
    let newCol = Math.round(col);
    if (Math.abs(offset) > colWidth * 0.25 || Math.abs(velocity) > 800)
      newCol += direction;
    newCol = Math.min(
      Math.max(newCol, 0),
      totalCols - Math.min(totalCols, visibleCols)
    );
    setCurrentCol(newCol);
    const targetX = -newCol * colWidth;
    animate(x, targetX, { type: "spring", stiffness: 260, damping: 30 });
    startAutoplay();
  };

  const moveToNextSlide = useCallback(() => {
    if (totalCols <= visibleCols) return;
    const newCol = (currentCol + 1) % (totalCols - visibleCols + 1);
    setCurrentCol(newCol);
    animate(x, -newCol * colWidth, {
      type: "spring",
      stiffness: 260,
      damping: 30,
    });
  }, [currentCol, totalCols, visibleCols, colWidth, x]);

  const { startAutoplay, stopAutoplay } = useAutoSlider({
    interval: 4000,
    enabled: totalCols > visibleCols,
    onTick: moveToNextSlide,
  });

  return (
    <div className={styles.carouselContainer}>
      <div
        className={`${styles.carouselViewport} ${styles.logoGridContainer}`}
        style={{
          width:
            totalCols <= visibleCols
              ? "100%"
              : `${colWidth * visibleCols - gap + 60}px`,
        }}
      >
        <motion.div
          ref={containerRef}
          className={styles.carouselTrack}
          drag={totalCols > visibleCols ? "x" : false}
          dragConstraints={{ left: -maxScrollX, right: 0 }}
          onDragStart={() => stopAutoplay()}
          onDragEnd={handleDragEnd}
          onMouseEnter={() => stopAutoplay()}
          onMouseLeave={() => startAutoplay()}
          style={{
            x,
            gap,
            justifyContent: totalCols <= visibleCols ? "center" : "flex-start",
          }}
        >
          {gridData.map((pair, colIdx) => (
            <div
              key={colIdx}
              style={{
                flex: "0 0 auto",
                width: itemSize,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: isMobile ? "20px" : "40px",
                zIndex: 2,
              }}
            >
              {pair.map(
                (logo, i) =>
                  logo && (
                    <div
                      key={i}
                      style={{
                        width: itemSize,
                        height: itemSize * 0.7,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        overflow: "hidden",
                        position: "relative",
                      }}
                      onDragStart={(e) => e.preventDefault()}
                    >
                      <Image
                        src={appendImageUrlFromAPI({ src: logo, size: "xl" })}
                        alt={`Đối tác ${colIdx * 2 + i}`}
                        fill
                        draggable={false}
                        style={{ objectFit: "contain" }}
                      />
                    </div>
                  )
              )}
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

/* ------------------- RIGHT CERTIFICATES ------------------- */
function RightPartnerSection({ papers }: { papers: string[] }) {
  const { isMobile, isTablet } = useWindowSize();
  const containerRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [currentCol, setCurrentCol] = useState(0);

  const gap = isMobile ? 20 : isTablet ? 20 : 32;
  const itemWidth = isMobile ? 130 : isTablet ? 160 : 220;
  const visibleCount = 2;
  const totalCols = papers?.length || 0;

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, [isMobile]);

  const colWidth = itemWidth + gap;
  const totalScrollWidth = totalCols * colWidth - gap;
  const maxScrollX = Math.max(totalScrollWidth - containerWidth, 0);

  const moveToNextSlide = useCallback(() => {
    if (totalCols <= visibleCount) return;
    const newCol = (currentCol + 1) % (totalCols - visibleCount + 1);
    setCurrentCol(newCol);
    animate(x, -newCol * colWidth, {
      type: "spring",
      stiffness: 260,
      damping: 32,
    });
  }, [currentCol, totalCols, visibleCount, colWidth, x]);

  const { startAutoplay, stopAutoplay } = useAutoSlider({
    interval: 4000,
    enabled: totalCols > visibleCount,
    onTick: moveToNextSlide,
  });

  const handleDragEnd = (_: any, info: any) => {
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    const direction = offset < 0 ? 1 : -1;
    let col = -x.get() / colWidth;
    let newCol = Math.round(col);
    if (Math.abs(offset) > colWidth * 0.25 || Math.abs(velocity) > 800)
      newCol += direction;
    newCol = Math.min(
      Math.max(newCol, 0),
      totalCols - Math.min(totalCols, visibleCount)
    );
    setCurrentCol(newCol);
    const targetX = -newCol * colWidth;
    animate(x, targetX, { type: "spring", stiffness: 260, damping: 32 });
    startAutoplay();
  };

  return (
    <div className={styles.carouselContainer}>
      <div
        className={styles.carouselViewport}
        style={{
          width:
            totalCols <= visibleCount
              ? "100%"
              : `${colWidth * visibleCount - gap}px`,
          zIndex: 2,
        }}
      >
        <motion.div
          ref={containerRef}
          className={styles.carouselTrack}
          drag={totalCols > visibleCount ? "x" : false}
          dragConstraints={{ left: -maxScrollX, right: 0 }}
          onDragStart={() => stopAutoplay()}
          onDragEnd={handleDragEnd}
          onMouseEnter={() => stopAutoplay()}
          onMouseLeave={() => startAutoplay()}
          style={{
            x,
            gap,
            padding: "4px",
            justifyContent: totalCols <= visibleCount ? "center" : "flex-start",
          }}
        >
          {papers?.map((img, i) => (
            <div
              key={i}
              style={{
                width: itemWidth,
                flex: "0 0 auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div
                className={styles.certificateBox}
                style={{
                  width: "100%",
                  height: isMobile ? 180 : isTablet ? 160 : 250,
                  boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
                  borderRadius: "2px",
                }}
                onDragStart={(e) => e.preventDefault()}
              >
                <Image
                  src={appendImageUrlFromAPI({ src: img, size: "xl" })}
                  alt={`Chứng chỉ ${i + 1}`}
                  fill
                  draggable={false}
                  style={{ objectFit: "contain" }}
                />
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

/* ------------------- DECORATION ------------------- */
function DecoImages({ isActive }: { isActive?: boolean }) {
  const decoImages = [
    {
      src: "/images/tea_deco.png",
      initial: { x: "-20vw", y: "20vh", opacity: 0 },
      animate: { x: "3vw", y: "0vh", opacity: 1 },
      exit: { x: "-20vw", y: "20vh", opacity: 0 },
      style: { bottom: 0, left: 0 },
    },
    {
      src: "/images/tea_deco_3.png",
      initial: { x: "20vw", y: "25vh", opacity: 0 },
      animate: { x: "-3vw", y: "0vh", opacity: 1 },
      exit: { x: "20vw", y: "25vh", opacity: 0 },
      style: { bottom: 0, right: 0 },
    },
    {
      src: "/images/tea_deco_2.png",
      initial: { x: 80, y: -180, opacity: 0 },
      animate: { x: 0, y: 0, opacity: 1 },
      exit: { x: 80, y: -180, opacity: 0 },
      style: { top: "20vh", right: 0, width: 180, height: 180 },
    },
  ];

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      <AnimatePresence>
        {isActive &&
          decoImages.map((img, i) => (
            <motion.img
              key={img.src}
              src={img.src}
              initial={img.initial}
              animate={img.animate}
              exit={img.exit}
              transition={{
                duration: 1.2,
                ease: [0.25, 0.8, 0.3, 1],
                delay: i * 0.15,
              }}
              style={{
                position: "absolute",
                width: 290,
                height: 290,
                ...img.style,
              }}
            />
          ))}
      </AnimatePresence>
    </div>
  );
}

/* ------------------- PARALLAX SECTION ------------------- */
export function ParallaxSection({
  title_1,
  title_2,
  desc_1,
  desc_2,
  picture_1,
  picture_2,
}: {
  title_1: string;
  title_2: string;
  desc_1: string;
  desc_2: string;
  picture_1: string;
  picture_2: string;
}) {
  useEffect(() => {
    const frames = document.querySelectorAll(".parallax-frame");
    if (!frames.length) return;

    let ticking = false;
    let viewportHeight = window.innerHeight;
    let lastScrollY = window.scrollY;

    const updateParallax = () => {
      frames.forEach((frame: any) => {
        const img = frame.querySelector("img");
        if (!img) return;

        const rect = frame.getBoundingClientRect();
        const progress =
          (rect.top + rect.height / 2 - viewportHeight / 2) / viewportHeight;

        const clamped = Math.max(-1, Math.min(1, progress));
        const translateY = clamped * viewportHeight * -0.68;

        img.style.transform = `translate3d(-50%, calc(-50% + ${translateY}px), 0)`;
      });

      ticking = false;
    };

    const onScroll = () => {
      const currentY = window.scrollY;
      if (Math.abs(currentY - lastScrollY) < 1) return;
      lastScrollY = currentY;

      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    };

    let resizeTimeout: NodeJS.Timeout;
    const onResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        viewportHeight = window.innerHeight;
        updateParallax();
      }, 200);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("load", updateParallax);

    setTimeout(updateParallax, 200);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("load", updateParallax);
    };
  }, []);

  return (
    <div className={styles.parallaxSection}>
      {/* Block 1 */}
      <div className={styles.container}>
        <div className={styles.parallaxBlock}>
          <div className={`${styles.imageFrame} parallax-frame`}>
            <img
              src={appendImageUrlFromAPI({ src: picture_1 })}
              alt="pic_1"
              className={styles.parallaxImg}
            />
          </div>
          <div className={styles.textBlock}>
            <h2 className={styles.textTitle}>{title_1}</h2>
            <div
              className={styles.textContent}
              dangerouslySetInnerHTML={{ __html: desc_1 }}
            />
          </div>
        </div>
      </div>

      {/* Block 2 */}
      <div className={styles.container}>
        <div className={`${styles.parallaxBlock} ${styles.parallaxReverse}`}>
          <div className={`${styles.imageFrame} parallax-frame`}>
            <img
              src={appendImageUrlFromAPI({ src: picture_2 })}
              alt="pic_2"
              className={styles.parallaxImg}
            />
          </div>
          <div className={styles.textBlock}>
            <h2 className={styles.textTitle}>{title_2}</h2>
            <div
              className={styles.textContent}
              dangerouslySetInnerHTML={{ __html: desc_2 }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
